/**
 * Welcome/Login/Register Page
 * Beautiful landing page with Google OAuth + Turnstile
 */
// @ts-nocheck - Temporary fix for react-icons type compatibility with Chakra UI
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FiSun, FiUsers, FiShield } from 'react-icons/fi';

import { TurnstileWidget } from '../components/auth/TurnstileWidget';
import { inviteService } from '../services/InviteService';
import { authService } from '../services/AuthService';
import { useAuth } from '../contexts/AuthContext';

export function WelcomePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, loading } = useAuth();

  // Debug log
  useEffect(() => {
    devLog('[WelcomePage] Loaded - isAuthenticated:', isAuthenticated, 'loading:', loading);
  }, [isAuthenticated, loading]);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && isAuthenticated) {
      window.location.href = '/';
    }
  }, [isAuthenticated, loading]);

  const [inviteCode, setInviteCode] = useState(searchParams.get('invite') || '');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [validating, setValidating] = useState(false);
  const [readyToLogin, setReadyToLogin] = useState(false);

  // Validate invite code
  const handleValidateInvite = async () => {
    if (!inviteCode) {
      setInviteValid(false);
      return;
    }

    setValidating(true);
    try {
      // Clean code before validation (remove whitespace)
      const cleanedCode = inviteCode.replace(/\s/g, '').toUpperCase();
      const result = await inviteService.validateInvite(cleanedCode);
      setInviteValid(result.valid);

      if (result.valid) {
        toast({
          title: 'Invite code validated',
          description: 'Please complete verification below',
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Invalid invite code',
          description: 'Please check your invite code and try again',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      setInviteValid(false);
      toast({
        title: 'Validation failed',
        description: 'Could not validate invite code',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setValidating(false);
    }
  };

  // Handle Turnstile verification
  const handleTurnstileVerify = (token: string) => {
    setTurnstileToken(token);
    setReadyToLogin(true);
    toast({
      title: 'Verification successful',
      description: 'You can now sign in with Google',
      status: 'success',
      duration: 3000,
    });
  };

  const handleTurnstileError = () => {
    setTurnstileToken(null);
    setReadyToLogin(false);
    toast({
      title: 'Verification failed',
      description: 'Please try again',
      status: 'error',
      duration: 3000,
    });
  };

  // Handle invite code change
  const handleInviteChange = (value: string) => {
    // Remove all whitespace and convert to uppercase
    const cleanedValue = value.replace(/\s/g, '').toUpperCase();
    setInviteCode(cleanedValue);
    setInviteValid(null);
    setTurnstileToken(null);
    setReadyToLogin(false);
  };

  // Store state for OAuth callback
  const handleGoogleLogin = () => {
    if (!readyToLogin) return;

    // Store invite and turnstile in sessionStorage for callback
    sessionStorage.setItem('invite_code', inviteCode);
    sessionStorage.setItem('turnstile_token', turnstileToken);

    // Trigger Google OAuth (new 2-step flow)
    authService.initiateGoogleLogin();
  };

  // Show nothing while checking auth
  if (loading) {
    return null;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header - matching Hora style exactly */}
      <Box
        as="header"
        h="60px"
        background="linear-gradient(45deg, rgba(255, 115, 84, 0.8) 0%, rgba(255, 184, 108, 0.6) 25%, rgba(108, 198, 255, 0.5) 50%, rgba(84, 255, 159, 0.4) 75%, rgba(0, 235, 255, 0.27) 100%)"
        backgroundSize="400% 400%"
        animation="gradientShift 108s ease-in-out infinite"
        boxShadow="0 2px 20px rgba(0,0,0,0.15)"
        px={5}
        position="sticky"
        top={0}
        zIndex={10}
        display="flex"
        alignItems="center"
      >
        <Text
          fontSize="24px"
          fontWeight="bold"
          color="rgba(255, 255, 255, 0.5)"
          fontFamily="var(--font-ui-heading)"
          letterSpacing="0.5px"
        >
          Bhrigu Hora
        </Text>
      </Box>

      <style>
        {`
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}
      </style>

      {/* Main Content */}
      <Container maxW="container.md" py={16}>
        <VStack spacing={8} align="stretch">
          {/* Hero Section */}
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              bgGradient="linear(to-r, orange.400, red.500, purple.500)"
              bgClip="text"
              lineHeight="1.4"
            >
              Welcome to Bhrigu Hora
            </Heading>
            <Text fontSize="xl" color="gray.600">
              Authentic Vedic Astrology powered by Swiss Ephemeris
            </Text>
            <HStack spacing={8} pt={4}>
              <VStack spacing={1}>
                <Icon as={FiSun} w={6} h={6} color="orange.500" />
                <Text fontSize="sm" color="gray.600">100% Vedic</Text>
              </VStack>
              <VStack spacing={1}>
                <Icon as={FiShield} w={6} h={6} color="blue.500" />
                <Text fontSize="sm" color="gray.600">Secure</Text>
              </VStack>
              <VStack spacing={1}>
                <Icon as={FiUsers} w={6} h={6} color="purple.500" />
                <Text fontSize="sm" color="gray.600">Invite-only</Text>
              </VStack>
            </HStack>
          </VStack>

          <Divider />

          {/* Login Card */}
          <Box
            bg="white"
            p={8}
            borderRadius="xl"
            boxShadow="xl"
            border="1px"
            borderColor="gray.200"
          >
            <VStack spacing={6} align="stretch">
              <Heading size="md" textAlign="center">
                Sign in or Register
              </Heading>

              {/* Step 1: Invite Code - Always shown first */}
              <FormControl isInvalid={inviteValid === false} isRequired>
                <FormLabel>Invite Code</FormLabel>
                <HStack>
                  <Input
                    placeholder="XXXX-XXXX-XXXX"
                    value={inviteCode}
                    onChange={(e) => handleInviteChange(e.target.value)}
                    size="lg"
                    fontFamily="mono"
                    isDisabled={inviteValid === true}
                  />
                  <Button
                    onClick={handleValidateInvite}
                    isLoading={validating}
                    colorScheme={inviteValid === true ? 'green' : 'blue'}
                    isDisabled={!inviteCode || inviteValid === true}
                  >
                    {inviteValid === true ? '✓' : 'Validate'}
                  </Button>
                </HStack>
                <FormErrorMessage>Invalid or expired invite code</FormErrorMessage>
              </FormControl>

              {/* Step 2: Turnstile Widget - Shown after invite validation */}
              {inviteValid === true && !turnstileToken && (
                <VStack spacing={2}>
                  <Text fontSize="sm" color="green.600" textAlign="center">
                    ✓ Invite code validated
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Please verify you're human
                  </Text>
                  <TurnstileWidget
                    onVerify={handleTurnstileVerify}
                    onError={handleTurnstileError}
                  />
                </VStack>
              )}

              {/* Step 3: Google Login Button - Shown after Turnstile */}
              {readyToLogin && (
                <>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="green.600" textAlign="center">
                      ✓ Verification successful
                    </Text>
                  </VStack>

                  <Box>
                    <Button
                      onClick={handleGoogleLogin}
                      size="lg"
                      width="full"
                      colorScheme="green"
                      leftIcon={
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                        </svg>
                      }
                    >
                      Continue with Google
                    </Button>
                  </Box>
                </>
              )}
            </VStack>
          </Box>

          {/* Footer Info */}
          <VStack spacing={2} pt={8}>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Bhrigu Hora uses invite-only registration to maintain quality and security
            </Text>
            <Text fontSize="xs" color="gray.500" textAlign="center">
              Don't have an invite? Ask a friend who's already using Bhrigu Hora
            </Text>
            <Divider pt={4} />
            <Text fontSize="sm" color="gray.600" textAlign="center" pt={4}>
              Already have an account?{' '}
              <Button
                as={Link}
                to="/login"
                variant="link"
                colorScheme="blue"
                fontWeight="semibold"
              >
                Sign in here
              </Button>
            </Text>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
}
